import type { CheckboxMenuSection } from '@/components/molecules/CheckboxMenu';
import {
    AUDIT_ACTOR_TYPES,
    AUDIT_METHODS,
    AUDIT_OUTCOMES,
    type AuditActorType,
    type AuditMethod,
    type AuditOutcome,
    type AuditTranslate,
} from '@/domain/auditLog';

function areaOf(action: string): string {
    const dot = action.indexOf('.');
    return dot > 0 ? action.slice(0, dot) : action;
}

/** Recorded values plus any chosen through a link but not recorded yet, so they can be unticked. */
function withSelected(recorded: readonly string[], selected: readonly string[]): string[] {
    return [...new Set([...recorded, ...selected])].sort();
}

/**
 * The recorded actions grouped by the part before the first dot, so "auth" ticks every
 * sign-in action at once. They come from the data rather than a list in code, so a new
 * action shows up without a release.
 */
export function actionSections(
    recorded: readonly string[],
    selected: readonly string[]
): CheckboxMenuSection[] {
    const byArea = new Map<string, string[]>();
    for (const action of withSelected(recorded, selected)) {
        const area = areaOf(action);
        byArea.set(area, [...(byArea.get(area) ?? []), action]);
    }
    return [...byArea].map(([area, codes]) => ({
        group: { id: area, label: area },
        options: codes.map((code) => ({ value: code, label: code })),
    }));
}

/** Entity types by their translated name; a type added later shows its raw name. */
export function entityTypeSections(
    recorded: readonly string[],
    selected: readonly string[],
    t: AuditTranslate
): CheckboxMenuSection[] {
    const options = withSelected(recorded, selected).map((type) => ({
        value: type,
        label: t(`auditLog.entityType.${type}`, { defaultValue: type }),
    }));
    return [{ options }];
}

export function actorTypeSections(t: AuditTranslate): CheckboxMenuSection<AuditActorType>[] {
    const options = AUDIT_ACTOR_TYPES.map((type) => ({
        value: type,
        label: t(`auditLog.actorType.${type}`),
    }));
    return [{ options }];
}

export function outcomeSections(t: AuditTranslate): CheckboxMenuSection<AuditOutcome>[] {
    const options = AUDIT_OUTCOMES.map((outcome) => ({
        value: outcome,
        label: t(`auditLog.outcome.${outcome}`),
    }));
    return [{ options }];
}

/** HTTP methods are the same in every language. */
export function methodSections(): CheckboxMenuSection<AuditMethod>[] {
    return [{ options: AUDIT_METHODS.map((method) => ({ value: method, label: method })) }];
}
